import React, { PureComponent } from 'react';
import TextAnchor from './TextAnchor';
import { selectIsStampingAllowed } from '../../redux/slicers/measureToolSlicer';
import { getAppState } from '../../redux/store';

const RotateGlyph = ({ color }) => (
  <svg x="-4" y="-4" width="8" height="8" viewBox="0 0 24 24">
    <path
      fill={color}
      d="M12 7C6.48 7 2 9.24 2 12C2 14.24 4.94 16.13 9 16.77V20L13 16L9 12V14.73C5.85 14.17 4 12.83 4 12C4 10.94 7.04 9 12 9C16.96 9 20 10.94 20 12C20 12.73 18.54 13.89 16 14.53V16.58C19.53 15.81 22 14.05 22 12C22 9.24 17.52 7 12 7Z"
    />
  </svg>
);

const EDGE_LENGTH = 30;
const OFFSET_EDGE_LENGTH = 15;
const OFFSET_TEXT_OFFSET = 16;
const QUARTER_CIRCLE = Math.PI / 2;

export default class LineMeasurement extends PureComponent {
  state = { midHover: false };

  constructor(props) {
    super(props);
    this.ref = React.createRef();
    this.handleClickOutside = this.handleClickOutside.bind(this);
    this.checkIfImageAreaClicked = this.checkIfImageAreaClicked.bind(this);
    this.renderAllLines = this.renderAllLines.bind(this);
    this.clickedLine = this.clickedLine.bind(this);
    this.onWindowKeyUp = this.onWindowKeyUp.bind(this);
  }

  onWindowKeyUp(val) {
    if (
      this.props?.line?.selected &&
      (val.keyCode === 8 || val.keyCode === 46) &&
      !document.getElementById('textStyles') && 
      !document.getElementsByClassName('delete-button')?.length
    ) {
      this.props.onDeleteButtonClick(this.props.line);
    }
  }

  checkIfImageAreaClicked(value) {
    const allClasses = value?.classList || [];
    const allToIncludeClass = ['measurement-layer-base', 'measurement-layer'];
    let isImgAreaClicked = false;
    for (let i = 0; i < allToIncludeClass.length; i++) {
      if (allClasses.value.includes(allToIncludeClass[i])) {
        isImgAreaClicked = true;
        break;
      }
    }
    return isImgAreaClicked;
  }

  handleClickOutside(event) {
    if (
      this.ref.current &&
      !this.ref.current.contains(event.target) &&
      this.checkIfImageAreaClicked(event?.target)
    ) {
      this.props.clickedOutsideAllLines && this.props.clickedOutsideAllLines();
    }
  }

  componentDidUpdate() {
    this.startGrabber?.addEventListener('mousedown', this.onStartMouseDown);
    this.midGrabber?.addEventListener('mousedown', this.onMidMouseDown);
    this.midGrabber?.addEventListener('mouseenter', this.onMidMouseEnter);
    this.midGrabber?.addEventListener('mouseleave', this.onMidMouseLeave);
    this.endGrabber?.addEventListener('mousedown', this.onEndMouseDown);
    this.startEdgeRotateHandle?.addEventListener('mousedown', this.onEdgeRotateMouseDown);
    this.endEdgeRotateHandle?.addEventListener('mousedown', this.onEdgeRotateMouseDown);
    document.addEventListener('mousemove', this.onMouseMove);
    window.addEventListener('mouseup', this.onMouseUp);
    window.addEventListener('blur', this.endDrag);
    document.addEventListener('click', this.handleClickOutside, true);
    window.addEventListener('keyup', this.onWindowKeyUp);
  }

  componentWillUnmount() {
    this.startGrabber?.removeEventListener('mousedown', this.onStartMouseDown);
    this.midGrabber?.removeEventListener('mousedown', this.onMidMouseDown);
    this.midGrabber?.removeEventListener('mouseenter', this.onMidMouseEnter);
    this.midGrabber?.removeEventListener('mouseleave', this.onMidMouseLeave);
    this.endGrabber?.removeEventListener('mousedown', this.onEndMouseDown);
    this.startEdgeRotateHandle?.removeEventListener('mousedown', this.onEdgeRotateMouseDown);
    this.endEdgeRotateHandle?.removeEventListener('mousedown', this.onEdgeRotateMouseDown);
    document.removeEventListener('mousemove', this.onMouseMove);
    window.removeEventListener('mouseup', this.onMouseUp);
    window.removeEventListener('blur', this.endDrag);
    document.removeEventListener('click', this.handleClickOutside, true);
    window.removeEventListener('keyup', this.onWindowKeyUp);
  }

  clickedLine(that, e) {
    if (that.props.isMeasureToolEditable) {
      that.props.updateSelection(that.props.line.id);
    }
  }

  renderAllLines(obj) {
    const { startX, startY, endX, endY } = obj;
    const deltaX = endX - startX;
    const deltaY = endY - startY;
    const rotate = Math.atan2(deltaY, deltaX);
    const isOffsetLayout = this.props.line.styleApplied?.layout === 'offset';
    const edgeLength = isOffsetLayout ? OFFSET_EDGE_LENGTH : EDGE_LENGTH;
    const edgeHalf = edgeLength / 2.0;
    const manualEdgeAngle = this.props.line.styleApplied?.edgeAngle;
    const hasManualEdgeAngle = typeof manualEdgeAngle === 'number';
    let edgeX;
    let edgeY;
    if (hasManualEdgeAngle) {
      const angleRad = (manualEdgeAngle * Math.PI) / 180;
      edgeX = Math.cos(angleRad) * edgeHalf;
      edgeY = Math.sin(angleRad) * edgeHalf;
    } else {
      edgeX = (edgeLength * Math.sin(rotate)) / 2.0;
      edgeY = (edgeLength * Math.cos(rotate)) / 2.0;
    }
    const handleFactor = edgeHalf > 0 ? 1 + 6 / edgeHalf : 1;
    const handleEdgeX = edgeX * handleFactor;
    const handleEdgeY = edgeY * handleFactor;
    const centerX = (startX + endX) / 2;
    const centerY = (startY + endY) / 2;
    let textX;
    let textY;
    let textRotate;
    if (isOffsetLayout) {
      const rotateIsSmall = Math.abs(rotate) <= QUARTER_CIRCLE;
      const textOffsetX = (rotateIsSmall ? -1 : 1) * OFFSET_TEXT_OFFSET * Math.sin(rotate);
      const textOffsetY = (rotateIsSmall ? 1 : -1) * OFFSET_TEXT_OFFSET * Math.cos(rotate);
      textX = centerX + textOffsetX;
      textY = centerY + textOffsetY;
      textRotate = Math.atan2(textOffsetY, textOffsetX) - QUARTER_CIRCLE;
    } else {
      textX = centerX;
      textY = centerY;
      textRotate = 0;
    }
    const lineLength = Math.hypot(deltaX, deltaY);

    const text = this.props.measureLine(this.props.line);
    if (!this.props.line.modified && !('axis' in this.props.line)) {
      // here, checking the axis key to prevent the value from calculating in case of the product's dimension shot on product page
      // if the axis key is not present, then it means the value needs to be calculated. Otherwise, not.
      this.props.line['textValue'] = text;
    }
    const rootClassName = 'line-measurement' + (this.state.midHover ? ' mid-hover' : '');

    const lineStroke = this.props.line.styleApplied.lineColor || 'yellow';
    const lineStrokeWidth = (this.props.line.styleApplied.lineSize || 2) + 'px';
    const textColorStyle = {
      color: this.props.line.styleApplied.textColor || 'yellow',
      fontSize: (this.props.line.styleApplied.textSize || '10') + 'pt',
    };
    if (this.props.line.styleApplied.textStyle === 'bold') {
      textColorStyle['fontWeight'] = 'bold';
    } else if (this.props.line.styleApplied.textStyle === 'italic') {
      textColorStyle['fontStyle'] = 'italic';
    } else if (this.props.line.styleApplied.textStyle === 'underline') {
      textColorStyle['textDecoration'] = 'underline';
    }

    const normalizedUnit = (this.props.measurementUnit || '').toString().trim().toLowerCase();
    let unitDisplay = this.props.measurementUnit || '';
    if (normalizedUnit === 'feet' || normalizedUnit === 'foot') {
      unitDisplay = "'";
    } else if (normalizedUnit === 'inches' || normalizedUnit === 'inch') {
      unitDisplay = '"';
    } else if (
      normalizedUnit === 'centimeter' ||
      normalizedUnit === 'centimeters' ||
      normalizedUnit === 'cm'
    ) {
      unitDisplay = 'cm';
    }
    const lineLabelText = `${this.props.line['textValue'] || text}${unitDisplay || ''}`;
    const textSizePt = Number.parseInt(this.props.line.styleApplied.textSize || '10', 10);
    const textSizePx = Math.max(10, (Number.isNaN(textSizePt) ? 10 : textSizePt) * (4 / 3));
    const estimatedLabelWidth = lineLabelText.length * textSizePx * 0.56;
    const desiredGapLength = Math.max(26, estimatedLabelWidth + 12);
    const maxAllowedGap = Math.max(0, lineLength - 10);
    const actualGapLength = Math.min(desiredGapLength, maxAllowedGap);

    const shouldSplitMidLine =
      !isOffsetLayout && lineLength > 18 && actualGapLength > 0;

    let firstMidSegmentX2 = endX;
    let firstMidSegmentY2 = endY;
    let secondMidSegmentX1 = startX;
    let secondMidSegmentY1 = startY;
    if (shouldSplitMidLine) {
      const unitX = deltaX / lineLength;
      const unitY = deltaY / lineLength;
      const halfGap = actualGapLength / 2;
      firstMidSegmentX2 = centerX - unitX * halfGap;
      firstMidSegmentY2 = centerY - unitY * halfGap;
      secondMidSegmentX1 = centerX + unitX * halfGap;
      secondMidSegmentY1 = centerY + unitY * halfGap;
    }

    return (
      <div className={rootClassName} ref={this.ref}>
        <svg
          className="measurement-svg"
          onMouseDown={(e) => this.clickedLine(this, e)}
          id={this.props.line.id}>
          <g className="grabber-group">
            <line
              className={`${this.props.isMeasureToolEditable ? 'grabber mid-grabber' : ''}`}
              x1={startX}
              y1={startY}
              x2={endX}
              y2={endY}
              ref={(e) => (this.midGrabber = e)}
            />
            <line
              style={{ stroke: lineStroke, strokeWidth: lineStrokeWidth }}
              className="line mid-line"
              x1={startX}
              y1={startY}
              x2={isOffsetLayout ? endX : firstMidSegmentX2}
              y2={isOffsetLayout ? endY : firstMidSegmentY2}
              ref={(e) => (this.midLine = e)}
            />
            {shouldSplitMidLine && (
              <line
                style={{ stroke: lineStroke, strokeWidth: lineStrokeWidth }}
                className="line mid-line"
                x1={secondMidSegmentX1}
                y1={secondMidSegmentY1}
                x2={endX}
                y2={endY}
                ref={(e) => (this.midLineSecond = e)}
              />
            )}
          </g>
          <g className="grabber-group">
            <circle
              r={this.props.line.selected ? '4' : '0'}
              cx={startX}
              cy={startY}
              fill="#F46A35"
              stroke="white"
              className="start-grabber-circle"
              ref={(e) => (this.startGrabber = e)}
            />
            {!this.props.line.selected && (
              <line
                className="grabber start-grabber"
                x1={startX - edgeX}
                y1={startY + edgeY}
                x2={startX + edgeX}
                y2={startY - edgeY}
                ref={(e) => (this.startGrabber = e)}
              />
            )}
            <line
              style={{
                stroke: lineStroke,
                strokeWidth: lineStrokeWidth,
              }}
              className="line start-line"
              x1={startX - edgeX}
              y1={startY + edgeY}
              x2={startX + edgeX}
              y2={startY - edgeY}
              ref={(e) => (this.startLine = e)}
            />
            {this.props.line.selected && this.props.isMeasureToolEditable && (
              <g
                className="edge-rotate-handle"
                transform={`translate(${startX + handleEdgeX}, ${startY - handleEdgeY})`}
                ref={(e) => (this.startEdgeRotateHandle = e)}>
                <circle r="8" fill="rgba(0,0,0,0.001)" />
                <circle
                  className="edge-rotate-handle-bg"
                  r="6"
                  fill="#ffffff"
                  stroke="#F46A35"
                  strokeWidth="1.2"
                />
                <RotateGlyph color="#F46A35" />
              </g>
            )}
          </g>
          <g className="grabber-group">
            <circle
              r={this.props.line.selected ? '4' : '0'}
              cx={endX}
              cy={endY}
              fill="#F46A35"
              stroke="white"
              className="end-grabber-circle"
              ref={(e) => (this.endGrabber = e)}
            />
            {!this.props.line.selected && (
              <line
                className="grabber end-grabber"
                x1={endX - edgeX}
                y1={endY + edgeY}
                x2={endX + edgeX}
                y2={endY - edgeY}
                ref={(e) => (this.endGrabber = e)}
              />
            )}
            <line
              style={{
                stroke: lineStroke,
                strokeWidth: lineStrokeWidth,
              }}
              className="line end-line"
              x1={endX - edgeX}
              y1={endY + edgeY}
              x2={endX + edgeX}
              y2={endY - edgeY}
              ref={(e) => (this.endLine = e)}
            />
            {this.props.line.selected && this.props.isMeasureToolEditable && (
              <g
                className="edge-rotate-handle"
                transform={`translate(${endX + handleEdgeX}, ${endY - handleEdgeY})`}
                ref={(e) => (this.endEdgeRotateHandle = e)}>
                <circle r="8" fill="rgba(0,0,0,0.001)" />
                <circle
                  className="edge-rotate-handle-bg"
                  r="6"
                  fill="#ffffff"
                  stroke="#F46A35"
                  strokeWidth="1.2"
                />
                <RotateGlyph color="#F46A35" />
              </g>
            )}
          </g>
        </svg>
        <TextAnchor
          x={textX}
          y={textY}
          rotate={textRotate}
          onDeleteButtonClick={this.onDeleteButtonClick}
          textColorStyle={textColorStyle}
          textDisplay={this.props.line['textValue']}
          axis={this.props.line.axis}
          measurementUnit={this.props.measurementUnit}
          changeTextVal={this.props.changeTextVal}
          line={this.props.line}
          isMeasureToolEditable={this.props.isMeasureToolEditable}
          legacyLayout={isOffsetLayout}
          selectLine={() => this.clickedLine(this)}></TextAnchor>
      </div>
    );
  }

  render() {
    // Line layout:
    if (
      document.getElementsByClassName('any-dragged')?.length ||
      this.props.mode ||
      document.getElementsByClassName('sketch-picker')?.length ||
      document.getElementById('textStyles') ||
      document.getElementById('lineStyles') ||
      this.props.line.selected
    ) {
      this.props.disablePan(true);
    } else {
      this.props.disablePan(false);
    }
    let startX = this.props.line.startX * this.props.parentWidth;
    let startY = this.props.line.startY * this.props.parentHeight;
    let endX = this.props.line.endX * this.props.parentWidth;
    let endY = this.props.line.endY * this.props.parentHeight;
    const dataToPass = { startX, startY, endX, endY };
    return this.renderAllLines(dataToPass);
  }

  onStartMouseDown = (event) => {
    if (event.button === 0 && this.props.isMeasureToolEditable) {
      this.startDragInProgress = true;
      event.preventDefault();
      this.onDragBegin(event.clientX, event.clientY);
    }
  };

  onMidMouseDown = (event) => {
    if (event.button === 0 && this.props.isMeasureToolEditable) {
      this.midDragInProgress = true;
      event.preventDefault();
      this.onDragBegin(event.clientX, event.clientY);
    }
  };

  onEndMouseDown = (event) => {
    if (event.button === 0 && this.props.isMeasureToolEditable) {
      this.endDragInProgress = true;
      event.preventDefault();
      this.onDragBegin(event.clientX, event.clientY);
    }
  };

  onEdgeRotateMouseDown = (event) => {
    if (event.button === 0 && this.props.isMeasureToolEditable) {
      this.edgeRotateDragInProgress = true;
      event.preventDefault();
      event.stopPropagation();
      const svgEl = event.currentTarget.ownerSVGElement;
      const rect = svgEl.getBoundingClientRect();
      const isStartCap = event.currentTarget === this.startEdgeRotateHandle;
      const anchor = isStartCap ? this.props.line : { startX: this.props.line.endX, startY: this.props.line.endY };
      this.edgeRotateCenterX = rect.left + anchor.startX * this.props.parentWidth;
      this.edgeRotateCenterY = rect.top + anchor.startY * this.props.parentHeight;
      this.lineAtPress = this.props.line;
      this.getAnnotationLayerClassList()?.add('any-dragged');
    }
  };

  onDragBegin = (eventX, eventY) => {
    this.mouseXAtPress = eventX;
    this.mouseYAtPress = eventY;
    this.lineAtPress = this.props.line;
    this.startXAtPress = this.props.line.startX * (this.props.parentWidth * this.props.scale);
    this.startYAtPress = this.props.line.startY * (this.props.parentHeight * this.props.scale);
    this.endXAtPress = this.props.line.endX * (this.props.parentWidth * this.props.scale);
    this.endYAtPress = this.props.line.endY * (this.props.parentHeight * this.props.scale);
  };

  onMouseMove = (event) => this.onDrag(event.clientX, event.clientY);

  onDrag = (eventX, eventY) => {
    const isStampingAllowd = selectIsStampingAllowed(getAppState())
    if (this.edgeRotateDragInProgress) {
      const dx = eventX - this.edgeRotateCenterX;
      const dy = eventY - this.edgeRotateCenterY;
      if (dx === 0 && dy === 0) return;
      let degrees = (Math.atan2(-dy, dx) * 180) / Math.PI;
      if (degrees < 0) degrees += 180;
      if (degrees >= 180) degrees -= 180;
      this.dragOccurred = true;
      this.props.onChange({
        ...this.props.line,
        styleApplied: { ...this.props.line.styleApplied, edgeAngle: degrees },
      });
      return;
    }
    if (
      (this.startDragInProgress || this.endDragInProgress || this.midDragInProgress) &&
      !this.dragOccurred
    ) {
      this.dragOccurred = true;
      this.toggleDragStyles();
    }
    if (this.startDragInProgress) {
      let startX = this.clamp(this.getXAfterDrag(this.startXAtPress, eventX));
      let startY = this.clamp(this.getYAfterDrag(this.startYAtPress, eventY));
      let endX = this.props.line.endX;
      let endY = this.props.line.endY;
      if (
        (this.props.isShiftPressed || isStampingAllowd) &&
        document.getElementsByClassName('any-dragged')?.length
      ) {
        let deltaX = startX - endX;
        let deltaY = startY - endY;
        const aspectRatio = this.props.parentWidth / this.props.parentHeight; // Calculate aspect ratio
        let angle = Math.atan2(deltaY, deltaX); // Adjust deltaY based on aspect ratio
        // Calculate the length of the line segment while considering the aspect ratio
        const length = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
        // Snap the angle to the nearest 45-degree increment
        const snappedAngle = Math.round(angle / (Math.PI / 4)) * (Math.PI / 4);
        // Calculate the adjusted end coordinates
        startX = endX + (Math.cos(snappedAngle) * length) / aspectRatio;
        startY = endY + Math.sin(snappedAngle) * length;
        deltaX = startX - endX;
        deltaY = startY - endY;
        angle = Math.atan2(deltaY, deltaX); // Adjust deltaY based on aspect ratio
        startX = endX + Math.cos(angle) * length;
        startY = endY + Math.sin(angle) * length;
        if (startX <= 1 && startX >= 0 && startY <= 1 && startY >= 0) {
          this.UpdatedX = startX;
          this.UpdatedY = startY;
        }
        startX = this.UpdatedX;
        startY = this.UpdatedY;
      }
      const startUpdate = { ...this.props.line, startX, startY };
      this.props.onChange(startUpdate);
    } else if (this.endDragInProgress) {
      let endX = this.clamp(this.getXAfterDrag(this.endXAtPress, eventX));
      let endY = this.clamp(this.getYAfterDrag(this.endYAtPress, eventY));
      let startX = this.props.line.startX;
      let startY = this.props.line.startY;
      if (
        (this.props.isShiftPressed || isStampingAllowd) &&
        document.getElementsByClassName('any-dragged')?.length
      ) {
        let deltaX = endX - startX;
        let deltaY = endY - startY;
        const aspectRatio = this.props.parentWidth / this.props.parentHeight; // Calculate aspect ratio
        let angle = Math.atan2(deltaY, deltaX); // Adjust deltaY based on aspect ratio
        // Calculate the length of the line segment while considering the aspect ratio
        let length = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
        // Snap the angle to the nearest 45-degree increment
        let snappedAngle = Math.round(angle / (Math.PI / 4)) * (Math.PI / 4);
        // Calculate the adjusted end coordinates
        endX = startX + (Math.cos(snappedAngle) * length) / aspectRatio; // Adjust endx based on aspect ratio
        endY = startY + Math.sin(snappedAngle) * length;
        deltaX = endX - startX;
        deltaY = endY - startY;
        angle = Math.atan2(deltaY, deltaX); // Adjust deltaY based on aspect ratio
        endX = startX + Math.cos(angle) * length;
        endY = startY + Math.sin(angle) * length;
        if (endX <= 1 && endX >= 0 && endY <= 1 && endY >= 0) {
          this.UpdatedX = endX;
          this.UpdatedY = endY;
        }
        endX = this.UpdatedX;
        endY = this.UpdatedY;
      }
      const endUpdate = { ...this.props.line, endX, endY };
      this.props.onChange(endUpdate);
    } else if (this.midDragInProgress) {
      let startX = this.getXAfterDrag(this.startXAtPress, eventX);
      let startY = this.getYAfterDrag(this.startYAtPress, eventY);
      let endX = this.getXAfterDrag(this.endXAtPress, eventX);
      let endY = this.getYAfterDrag(this.endYAtPress, eventY);
      const deltaX = endX - startX;
      const deltaY = endY - startY;

      // Don't let the line be dragged outside the layer bounds:
      if (startX < 0) {
        startX = 0;
        endX = deltaX;
      } else if (startX > 1) {
        startX = 1;
        endX = 1 + deltaX;
      }
      if (startY < 0) {
        startY = 0;
        endY = deltaY;
      } else if (startY > 1) {
        startY = 1;
        endY = 1 + deltaY;
      }
      if (endX < 0) {
        startX = -deltaX;
        endX = 0;
      } else if (endX > 1) {
        startX = 1 - deltaX;
        endX = 1;
      }
      if (endY < 0) {
        startY = -deltaY;
        endY = 0;
      } else if (endY > 1) {
        startY = 1 - deltaY;
        endY = 1;
      }
      this.props.onChange({ ...this.props.line, startX, startY, endX, endY });
    }
  };

  getXAfterDrag = (xAtPress, clientX) =>
    (xAtPress + clientX - this.mouseXAtPress) / (this.props.parentWidth * this.props.scale);

  getYAfterDrag = (yAtPress, clientY) =>
    (yAtPress + clientY - this.mouseYAtPress) / (this.props.parentHeight * this.props.scale);

  onMouseUp = (event) => this.endDrag();

  endDrag = () => {
    if (this.edgeRotateDragInProgress) {
      this.edgeRotateDragInProgress = false;
      this.getAnnotationLayerClassList()?.remove('any-dragged');
      if (this.dragOccurred) {
        this.dragOccurred = false;
        this.props.onCommit(this.props.line, 'drag-end');
      }
      return;
    }
    if (this.dragOccurred) {
      this.toggleDragStyles();
      this.dragOccurred = false;
    }
    const anyDragAttempted =
      this.startDragInProgress || this.midDragInProgress || this.endDragInProgress;
    if (this.startDragInProgress) {
      this.startDragInProgress = false;
    }
    if (this.midDragInProgress) {
      this.midDragInProgress = false;
    }
    if (this.endDragInProgress) {
      this.endDragInProgress = false;
    }
    if (anyDragAttempted && this.didValuesChange()) {
      this.props.onCommit(this.props.line, 'drag-end');
    }
  };

  didValuesChange = () =>
    this.props.line.startX !== this.lineAtPress.startX ||
    this.props.line.startY !== this.lineAtPress.startY ||
    this.props.line.endX !== this.lineAtPress.endX ||
    this.props.line.endY !== this.lineAtPress.endY;

  onMidMouseEnter = (event) => this.setState({ ...this.state, midHover: this.props.isMeasureToolEditable });

  onMidMouseLeave = (event) => this.setState({ ...this.state, midHover: false });

  getAnnotationLayerClassList = () => {
    return this.ref?.current?.parentElement.classList;
  };

  clamp = (value) => Math.min(1, Math.max(0, value));

  toggleDragStyles = () => {
    if (this.startDragInProgress) {
      this.startLine?.classList.toggle('dragged');
      this.startGrabber?.classList.toggle('dragged');
      this.getAnnotationLayerClassList().toggle('line-start-dragged');
    }
    if (this.midDragInProgress) {
      this.startLine?.classList.toggle('dragged');
      this.midLine?.classList.toggle('dragged');
      this.midLineSecond?.classList.toggle('dragged');
      this.endLine?.classList.toggle('dragged');
      this.startGrabber?.classList.toggle('dragged');
      this.midGrabber?.classList.toggle('dragged');
      this.endGrabber?.classList.toggle('dragged');
      this.getAnnotationLayerClassList().toggle('line-mid-dragged');
    }
    if (this.endDragInProgress) {
      this.endLine?.classList.toggle('dragged');
      this.endGrabber?.classList.toggle('dragged');
      this.getAnnotationLayerClassList().toggle('line-end-dragged');
    }
    this.getAnnotationLayerClassList().toggle('any-dragged');
  };

  onDeleteButtonClick = () => this.props.onDeleteButtonClick(this.props.line);
}
